import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

/*
 * Skeleton — DS alignment (Phase 3C)
 *
 * DS Page: 8403137
 *
 * Colour:    bg-muted (neutral-050 = #F2F4F6) — the DS specifies a neutral,
 *            low-contrast placeholder. Previously used bg-accent (purple-050)
 *            which added an unintended purple tint.
 * Animation: animate-pulse — subtle, not high-contrast. DS advises "subtle".
 * Shape:     Two variants — rounded (default, for text/media) and circle (for avatars).
 *            DS anatomy: "circle and square" shapes as building blocks.
 */

const skeletonHeightClasses = {
  3: 'h-3',
  4: 'h-4',
  5: 'h-5',
  8: 'h-8',
  9: 'h-9',
  10: 'h-10',
  full: 'h-full',
} as const

const skeletonWidthClasses = {
  16: 'w-16',
  24: 'w-24',
  28: 'w-28',
  32: 'w-32',
  40: 'w-40',
  48: 'w-48',
  52: 'w-52',
  64: 'w-64',
  '1/2': 'w-1/2',
  '2/3': 'w-2/3',
  '3/4': 'w-3/4',
  full: 'w-full',
} as const

export namespace Skeleton {
  export type Height = keyof typeof skeletonHeightClasses
  export type Width = keyof typeof skeletonWidthClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Height of the skeleton. */
    height?: Height
    /** Width of the skeleton. */
    width?: Width
    /**
     * Shape variant.
     * - 'rounded' (default) — pill/rectangle shape for text and media placeholders.
     * - 'circle'            — circular shape for avatar placeholders.
     */
    shape?: 'rounded' | 'circle'
  }
}

function Skeleton({ height, width, shape = 'rounded', className, ...props }: Skeleton.Props) {
  return (
    <div
      data-slot="skeleton"
      className={cn(
        'animate-pulse bg-muted',
        shape === 'circle' ? 'rounded-full' : 'rounded-md',
        height !== undefined && skeletonHeightClasses[height],
        width !== undefined && skeletonWidthClasses[width],
        className,
      )}
      {...props}
    />
  )
}

export { Skeleton }
